<template>
    <div class="col-lg-9">
        <div class="d-flex align-items-center mb-4">
            <h4 class="text-7">Stored Accounts</h4>
            <div class="ml-auto" v-if="data.content.data.length > 0">
                <router-link
                    :to="`/business/stored-accounts/add`"
                    class="btn btn-primary btn-sm text-white shadow-none mr-2"
                >
                    <i class="fas fa-plus-circle mr-2"></i>Add Account
                </router-link>
                <router-link
                    :to="`/business/stored-accounts/create-batch`"
                    class="btn btn-success btn-sm text-white shadow-none"
                >
                    <i class="fas fa-layer-group mr-2"></i>Add Batch
                </router-link>
            </div>
        </div>

        <div v-if="data.content.data.length > 0">
            <hr>
            <p class="d-flex flex-row align-items-center">
                <span>
                     <i class="fas fa-info-circle mr-2"></i>
                </span>
                <span>
                     Groups organize stored accounts into categories called batches, enabling bulk management of related stored accounts.
                </span>
            </p>
            <hr>
        </div>

        <!-- Stored Accounts Table -->
        <form v-if="data.content.data.length > 0" @submit.prevent="data.fetch()" class="row my-4 align-items-center pr-3">
            <div class="col-lg-2">
                <select @change="data.fetch()" v-model="data.filters.size" class="custom-select" required>
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="25">25</option>
                    <option value="30">30</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </select>
            </div>
            <div class="input-group col-lg-4 ml-auto">
                <div class="input-group-prepend">
                    <span class="input-group-text bg-white"><i class="fas fa-search"></i></span>
                </div>
                <input type="text" class="form-control" v-model="data.filters.search" placeholder="Search">
            </div>

            <button @click.prevent="data.fetch()"
                    :class="['btn btn-primary btn-sm text-white px-3 shadow-none', data.loading ? 'btn-loading' : '']">
                <i class="fas fa-arrow-alt-circle-right"></i>
            </button>
        </form>
        <div v-if="data.content.data.length <= 0">
            <div class="d-flex flex-column alert alert-info p-4">
                <h4 class="text-5 font-weight-400">Get started with Stored Accounts</h4>
                <div class="d-flex flex-column font-weight-300">
                <span>
                    Stored accounts allow businesses to manage frequently used account details for quick access and transactions.

                </span>
                    <span class="font-weight-400 mt-2">
                    3 Steps to Get Started:
                </span>
                    <ol >
                        <li>
                            Create a Group – Organize your stored accounts by creating a group that will hold related batches.
                        </li>
                        <li>
                            Add a Batch – Within a group, create a batch to categorize and manage a set of stored accounts.
                        </li>
                        <li>
                            Store Accounts – Add individual stored accounts to a batch for quick access and streamlined transactions.
                        </li>
                    </ol>
                    <span class="mt-2">
                   Start now – add a stored account or upload a batch!
                </span>
                    <span class="mt-3" >
                    <router-link
                        :to="`/business/stored-accounts/add`"
                        class="btn btn-primary btn-sm text-white shadow-none mr-2"
                    >
                        <i class="fas fa-plus-circle mr-2"></i>Add Account
                    </router-link>
                    <router-link
                        :to="`/business/stored-accounts/create-batch`"
                        class="btn btn-success btn-sm text-white shadow-none"
                    >
                        <i class="fas fa-layer-group mr-2"></i>Add Batch
                    </router-link>
                </span>
                </div>
            </div>
        </div>
        <div v-if="data.content.data.length > 0" class="bg-light shadow-sm rounded py-4 mb-4">
            <div >
                <div class="d-flex justify-content-start align-items-center mb-4 px-4">
                    <div class="mb-0 d-flex align-items-center">
                        <h3 class="text-4 font-weight-700 mb-0 mr-2">
                            Groups
                        </h3>
                    </div>
                </div>
                <!-- Title =============================== -->
                <div class="transaction-title py-2 px-4">
                    <div class="row">
                        <div class="col-3 col-sm-3"><span class="">Name</span></div>
                        <div class="col col-sm-2 d-none d-sm-block">Batches</div>
                        <div class="col col-sm-2 d-none d-sm-block">Accounts</div>
                        <div class="col col-sm-2 d-none d-sm-block">Status</div>
                        <div class="col-auto col-sm-3 text-center">Actions</div>
                    </div>
                </div>
                <!-- Title End -->

                <div class="transaction-list">
                    <div v-for="row in data.content.data" :key="row.id" class="transaction-item d-block px-4 py-3">
                        <div class="row align-items-center flex-row">
                            <div class="col-3 col-sm-3">
                                <span class="row-text">{{ row.group_name }}</span>
                            </div>
                            <div class="col col-sm-2 d-none d-sm-block">
                                <span class="row-text">{{ row.batches_count || 0 }}</span>
                            </div>
                            <div class="col col-sm-2">
                                <span class="row-text">{{ row.total_accounts || 0 }}</span>
                            </div>
                            <div class="col col-sm-2">
                                <span class="row-text">{{ row.status || 0 }}</span>
                            </div>
                            <div class="col-auto col-sm-3 d-flex justify-content-end">
                                <router-link
                                    :to="`/business/stored-accounts/${row.id}/group`"
                                    class="btn btn-primary btn-sm btn-extra-small mr-2"
                                >
                                    <i class="fas fa-eye"></i>
                                </router-link>
                                <button @click="confirmSuspend(row)"
                                        :class="['btn btn-sm btn-extra-small', row.status === 'ACTIVE' ? 'btn-outline-success' : 'btn-success']">
                                    <i :class="row.status === 'ACTIVE' ? 'fas fa-ban' : 'fas fa-check'"></i>
                                    {{ row.status === 'ACTIVE' ? 'Suspend' : 'Activate' }}
                                </button>
                                <button @click="confirmDelete(row)"
                                        class="btn btn-danger btn-sm btn-extra-small ml-2">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Transaction List End -->
            </div>
        </div>
    </div>
</template>

<script>
import Data from "../../../core/data/Data";

export default {
    name: "Index",
    data() {
        return {
            data: new Data({
                url: "/business/business-stored-accounts/groups/list",
                prefix: "list"
            }),
            showModal: false,
            selectedAccount: {},
        };
    },
    methods: {
        confirmSuspend(group) {
            let newStatus = group.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
            let confirmationMessage = `Are you sure you want to ${newStatus === 'ACTIVE'? 'ACTIVATE': 'SUSPEND'} the group "${group.group_name}"?`;
            window.alerts.confirm(confirmationMessage).then((confirmed) => {
                if (confirmed) {
                    this.setGroupStatus(group, newStatus);
                    this.data.fetch();
                }
            });
        },

        setGroupStatus(group, status) {
            axios.put(`/business/business-stored-accounts/groups/${group.id}/status/${status}`)
                .then(response => {
                    window.alerts.success(`Group ${status} successfully`).then(() => {
                        this.data.fetch();
                    });
                })
                .catch(error => {
                    window.alerts.error(error.response);
                });
        },

        confirmDelete(group) {
            let confirmationMessage = `Are you sure you want to delete the group "${group.group_name}"? This action cannot be undone.`;
            window.alerts.confirm(confirmationMessage).then((confirmed) => {
                if (confirmed) {
                    this.deleteGroup(group);
                    this.data.fetch();
                }
            });
        },

        deleteGroup(group) {
            axios.delete(`/business/business-stored-accounts/groups/${group.id}`)
                .then(response => {
                    window.alerts.success(response.data.message).then(() => {
                        this.data.fetch();
                    });
                })
                .catch(error => {
                    window.alerts.error(error.response);
                });
        },


    },
    mounted() {
        this.data.fetch()
    }
}
</script>

<style scoped>
.row-text{
    color: #252c65;
}
.btn-extra-small {
    padding: 6px 6px;
    font-size: 12px;
    line-height: 1.2;
}
</style>
